import * as React from 'react';
import { useCallback, useMemo } from 'react';
import MediaQuery from '../../../../../MediaQuery/MediaQuery';
import cn from 'classnames';
import { SeatComfort, SeatMapRestrictions } from '@websky/graphql';
import { findPassengerWithThisSeat } from '../../Seat/utils';
import {
	CombinedDirection,
	RowPart,
	RowWithParts,
	Seat as SeatType,
	EdgeSeatsNumbers,
	SeatService,
	ServicesMap
} from '../../../../types';
import { GdsServiceById } from '@shared/lib/gdsService';
import { useTheme } from '../../../../../theme';
import { PassengerState } from '../../../../../Checkout/store/passengers/reducers';
import Seat from '../../Seat/Seat';
import { getAllowedPassengerSeatServices, getSeatAvailability } from './utils';
import { useTranslation } from 'react-i18next';
import { cabinCodeComfortMap, getActualService, getMinPriceOfServiceClass } from '../../utils';
import { useRenderers } from '../../../../../renderProps';
import { useConfig } from '../../../../../context';
import { useDoubleSeat } from '../../../../hooks';
import TechnicalFacilities from './TechnicalFacilities/TechnicalFacilities';
import SeatPrice from '../../../SeatPrice/SeatPrice';
import { getSeatPrice } from '../../../../utils';
import { isZeroPrice } from '@shared/lib/money';
import { getCollapsedSeatsNumbers } from '../utils';

interface Props {
	partService: string;
	partIndex: number;
	part: RowPart;
	nextPartService?: SeatService;
	prevPartService?: SeatService;
	serviceId: string;
	passengerId: string;
	segmentId: string;
	isSeatMapWide: boolean;
	passengers: PassengerState[];
	services: ServicesMap;
	allSeatServices: ServicesMap;
	gdsServiceById: GdsServiceById;
	isCheckinMode: boolean;
	onSelect: (seat: SeatType[], passengerId: string) => void;
	newClass?: boolean;
	onClear?: () => void;
	isStatic?: boolean;
	rows: RowWithParts[];
	rowIndex: number;
	activeDoubleSeats?: boolean;
	rowsWithDifferentClasses: Map<number, SeatComfort>;
	activeFilters: SeatMapRestrictions[];
	edgeSeatsNumbers: EdgeSeatsNumbers;
}

const Parts: React.FunctionComponent<Props> = React.memo(
	({
		part,
		nextPartService,
		prevPartService,
		partIndex,
		passengerId,
		serviceId,
		segmentId,
		isSeatMapWide,
		passengers,
		allSeatServices,
		gdsServiceById,
		partService,
		isCheckinMode,
		onSelect,
		newClass,
		onClear,
		isStatic,
		rows,
		rowIndex,
		activeDoubleSeats,
		rowsWithDifferentClasses,
		activeFilters,
		edgeSeatsNumbers
	}) => {
		const theme = useTheme('SeatMap').SeatMapStyles;
		const { t, i18n } = useTranslation('SeatMap');

		const { SeatMapSeat: SeatMapSeatOverride = Seat } = useRenderers();

		const nextPartServiceId = nextPartService?.comfort;
		const prevPartServiceId = prevPartService?.comfort;
		const passengerServices = allSeatServices?.[segmentId]?.[passengerId];
		const actualService = React.useMemo(() => getActualService(passengerServices, part), [passengerServices, part]);
		const minServicePrice =
			newClass && actualService
				? getMinPriceOfServiceClass(rows, rowIndex, actualService, rowsWithDifferentClasses)
				: null;

		const isAllSeatsFree = React.useMemo(() => part.seats.every(seat => isZeroPrice(getSeatPrice(seat))), [
			part.seats
		]);

		const {
			global: {
				companyInfo: { iataCode },
				seatMap: { splitByCabin, showPriceInSeat, collapseEmptySeats }
			}
		} = useConfig();

		const showSeatPriceNotServicePrice = ['DV', 'RM'].includes(iataCode);

		const row = rows[rowIndex];

		const collapsedSeatsNumbers = React.useMemo(() => (collapseEmptySeats ? getCollapsedSeatsNumbers(row) : null), [
			row,
			collapseEmptySeats
		]);

		const {
			combineWith,
			selectedDoubleSeatsBySegments,
			combinedSeat,
			onSelectHandler,
			onMouseEnter,
			onMouseLeave
		} = useDoubleSeat(activeDoubleSeats, segmentId, part.seats, passengers, onSelect);

		const renderServiceHeader = useCallback(
			(comfort: SeatComfort) => {
				if (isStatic && partIndex !== 0) {
					return null;
				}

				return (
					<div
						className={cn(theme.row__serviceHeader, {
							[theme.row__serviceHeader_comfort]: comfort === SeatComfort.Comfort,
							[theme.row__serviceHeader_preferred]: comfort === SeatComfort.Preferred,
							[theme.row__serviceHeader_free]: isAllSeatsFree
						})}
					>
						<div className={theme.row__serviceHeader__wrapper}>
							{i18n.exists(`SeatMap:SeatMap_${comfort}`) && (
								<span className={theme.row__serviceHeader__serviceName}>
									{t(`SeatMap:SeatMap_${comfort}`)}
								</span>
							)}

							{minServicePrice && (
								<SeatPrice
									className={theme.row__serviceHeader__price}
									priceFromClassName={theme.row_serviceHeader_from}
									priceFromAfterClassName={theme.row_serviceHeader_from_after}
									money={minServicePrice}
									priceFrom={true}
								/>
							)}
						</div>
					</div>
				);
			},
			[minServicePrice]
		);

		const delimiter = useMemo(() => {
			if (!newClass || part.service?.isAisle || showPriceInSeat) {
				return null;
			}

			if (splitByCabin) {
				const rowService = cabinCodeComfortMap.get(rows[rowIndex].cabinCode);

				return renderServiceHeader(rowService);
			} else {
				if (actualService) {
					return renderServiceHeader(actualService);
				}
			}

			return null;
		}, [splitByCabin, part, newClass, actualService, minServicePrice, showPriceInSeat]);

		return (
			<div className={theme.row__PartWrpWithTechFacilities}>
				<div
					className={cn(theme.row__rowPartWrp, {
						[theme.row__seatWrapper_newClass]: newClass && !showPriceInSeat,
						[theme.row__rowPartWrp_aisle]: part.service?.isAisle,
						[theme.row__rowPartWrp_static]: isStatic
					})}
					key={partIndex}
				>
					<MediaQuery minWidth={'tablet'}>
						<div
							className={cn(theme.row__seatWrapper, {
								[theme.row__seatWrapper_boundaryRight]: partService !== nextPartServiceId,
								[theme.row__seatWrapper_boundaryLeft]: partService !== prevPartServiceId,
								[theme.row__seatWrapper_service_business]: partService === SeatComfort.Comfort,
								[theme.row__seatWrapper_service_preferred]: partService === SeatComfort.Preferred,
								[theme.row__seatWrapper_service_standard]: partService === SeatComfort.Standart,
								[theme.row__seatWrapper_service_business_wide]:
									partService === SeatComfort.Comfort && isSeatMapWide,
								[theme.row__seatWrapper_service_preferred_wide]:
									partService === SeatComfort.Preferred && isSeatMapWide,
								[theme.row__seatWrapper_service_standard_wide]:
									partService === SeatComfort.Standart && isSeatMapWide
							})}
							data-service-id={serviceId}
						/>
					</MediaQuery>

					{delimiter}

					{part.seats.map((seat, index) => {
						const currentPassenger = passengers.find(passenger => passenger.id === passengerId);
						const selectedSeat = currentPassenger.seats?.find(seat => seat.segmentId === segmentId);

						const prevSeat = part.seats?.[index - 1];
						const nextSeat = part.seats?.[index + 1];

						let actualSeat = seat;
						const occupiedBy = findPassengerWithThisSeat(actualSeat, passengers, segmentId);

						if (
							(actualSeat?.seatService || !!actualSeat?.seatServices?.length) &&
							!showSeatPriceNotServicePrice
						) {
							const allowedPassengerSeatService = getAllowedPassengerSeatServices(
								passengerServices,
								seat
							);

							if (!!allowedPassengerSeatService?.length) {
								actualSeat = { ...seat };
								actualSeat.services = { ...allowedPassengerSeatService };
								actualSeat.service = { ...allowedPassengerSeatService[0] };
								actualSeat.seatService = { ...allowedPassengerSeatService[0] };
								actualSeat.price = {
									amount: allowedPassengerSeatService.reduce(
										(prev, currentService) => prev + (currentService.price.amount || 0),
										0
									),
									currency: allowedPassengerSeatService[0].price.currency
								};
							}
						}

						const hasConfirmedSeat = currentPassenger.seats?.some(
							seat => seat?.isChangeable === false && seat.segmentId === segmentId
						);

						let isAvailable = getSeatAvailability({
							seat: actualSeat,
							passenger: currentPassenger,
							activeFilters,
							segmentId
						});

						if (isCheckinMode) {
							if (hasConfirmedSeat) {
								isAvailable = selectedSeat?.row === actualSeat.number;
							}

							if (actualSeat?.seatService) {
								isAvailable = isAvailable && actualSeat.seatService.canBeAdded;
							}
						}

						const isConfirmed = hasConfirmedSeat && selectedSeat?.number === actualSeat.number;
						const hasCombinedSeats = seat.combinedWith && Object.keys(seat.combinedWith).length > 0;
						const isSelectedDoubleSeat = selectedDoubleSeatsBySegments.get(segmentId)?.has(seat.number);
						const isOccupiedDoubleSeat = occupiedBy && isSelectedDoubleSeat;
						const seatPositionInRow = edgeSeatsNumbers.get(actualSeat.number);

						if (activeDoubleSeats) {
							isAvailable = isAvailable && hasCombinedSeats && !hasConfirmedSeat;

							if (
								!occupiedBy &&
								((!prevSeat &&
									nextSeat &&
									findPassengerWithThisSeat(nextSeat, passengers, segmentId)) ||
									(!nextSeat &&
										prevSeat &&
										findPassengerWithThisSeat(prevSeat, passengers, segmentId)))
							) {
								isAvailable = false;
							} else if (occupiedBy && !isSelectedDoubleSeat) {
								isAvailable = false;
							}
						}

						let isCombinedWith = activeDoubleSeats ? combineWith?.[seat.number] : null;

						if (
							isOccupiedDoubleSeat &&
							prevSeat &&
							selectedDoubleSeatsBySegments.get(segmentId)?.has(prevSeat.number) &&
							findPassengerWithThisSeat(prevSeat, passengers, segmentId)
						) {
							isCombinedWith = CombinedDirection.Left;
						} else if (
							isOccupiedDoubleSeat &&
							nextSeat &&
							selectedDoubleSeatsBySegments.get(segmentId)?.has(nextSeat.number) &&
							findPassengerWithThisSeat(nextSeat, passengers, segmentId)
						) {
							isCombinedWith = CombinedDirection.Right;
						}

						const onMouseEnterHandler = () => onMouseEnter(seat, segmentId);

						return (
							<SeatMapSeatOverride
								key={actualSeat.number}
								isSmall={isSeatMapWide}
								gdsServiceById={gdsServiceById}
								seat={actualSeat}
								isAvailable={isAvailable && !hasConfirmedSeat}
								occupiedBy={occupiedBy}
								isConfirmed={hasConfirmedSeat}
								segmentId={segmentId}
								passengerId={passengerId}
								onClear={onClear}
								onSelect={isConfirmed ? () => null : onSelectHandler}
								onMouseEnter={onMouseEnterHandler}
								onMouseLeave={onMouseLeave}
								isCombinedWith={isCombinedWith}
								combinedSeat={combinedSeat}
								passenger={currentPassenger}
								isSeatMapWide={isSeatMapWide}
								seatPositionInRow={seatPositionInRow}
								isCollapsed={!!collapsedSeatsNumbers?.has(actualSeat.number)}
							/>
						);
					})}
				</div>

				<TechnicalFacilities part={part} isSmall={isSeatMapWide} />
			</div>
		);
	}
);

export default Parts;
